import { Component, Input } from '@angular/core';
import { AbstractControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ControlTypes } from '@nx-customer-apps/shared/enums';
import { CustomAnswerRadio, TypesHTMLHelper } from '@nx-customer-apps/shared/utils';
import { ComponentsModule, fadeIn, fadeOut, HeatLoadCalculationMethod, slideDown } from '@nx-customer-apps/components';
import { RadioOption } from '@nx-customer-apps/shared/interfaces';
import { NgClass } from '@angular/common';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NgxMaskDirective } from 'ngx-mask';
import { TranslatePipe } from '@ngx-translate/core';
import { AsPipe } from '../../pipes';

@Component({
    selector: 'vp-custom-answer-radio',
    templateUrl: './custom-answer-radio.component.html',
    styleUrls: ['./custom-answer-radio.component.scss'],
    animations: [slideDown, fadeIn, fadeOut],
    imports: [
        ReactiveFormsModule,
        NgClass,
        MatRadioGroup,
        MatRadioButton,
        ComponentsModule,
        MatFormField,
        MatLabel,
        MatInput,
        NgxMaskDirective,
        MatHint,
        MatError,
        TranslatePipe,
        AsPipe
    ]
})
export class CustomAnswerRadioComponent {
    @Input() question: CustomAnswerRadio;
    @Input() form: FormGroup;
    readonly CONTROL_TYPES: typeof ControlTypes = ControlTypes;
    readonly TYPES = TypesHTMLHelper;
    public selectedOption: RadioOption | undefined;

    ngOnInit() {
        this.setRadio();
        this.setSelectedOption(this.radio.value);
    }

    public get formGroup(): AbstractControl {
        return this.form.get((this.question as CustomAnswerRadio).groupName) as AbstractControl;
    }

    public get isGroupInvalid(): boolean {
        return Boolean(this.formGroup?.errors);
    }

    public get isGroupTouched(): boolean {
        return !!this.isRadioTouched && !!this.isTextTouched;
    }

    public get groupErrorMessage(): string {
        const errorMessage = this.formGroup?.errors?.['message'];
        return errorMessage || (this.question as CustomAnswerRadio)?.defaultErrorMessage;
    }

    public get radio(): AbstractControl {
        return this.form.get(this.question.groupName)!.get(this.question.questions[0].key)!;
    }

    public get isRadioInvalid(): boolean {
        return this.radio.invalid;
    }

    public get isRadioTouched(): boolean {
        return this.radio.touched;
    }

    public get text(): AbstractControl {
        return this.form.get(this.question.groupName)!.get(this.question.questions[1].key)!;
    }

    public get isTextInvalid(): boolean {
        return this.text.invalid;
    }

    public get isTextTouched(): boolean {
        return this.text.touched;
    }

    public get textErrorMessage(): string {
        const errorMessage = this.text.errors?.['message'];
        return errorMessage || (this.question as CustomAnswerRadio)?.questions[1].defaultErrorMessage;
    }

    public onTextFieldFocus(): void {
        this.radio?.reset(null);
    }

    private setRadio(): void {
        this.radio?.valueChanges?.subscribe(value => {
            if (value === this.question.customAnswerOptionValue) {
                this.text?.addValidators(Validators.required);
                this.text?.markAsUntouched();
            } else {
                this.text?.removeValidators(Validators.required);
            }
        });
    }

    /**
     * Setting selected option is needed only to manage the display of special ui elements related with the option.
     * For example, the info message. It does not play any role in the form value handling.
     */
    public setSelectedOption(value: HeatLoadCalculationMethod): void {
        this.selectedOption = this.question.questions[0].options.find(option => option.value === value)!;
    }
}
